import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Skeleton,
} from "@chakra-ui/react";
import { useAtom } from "jotai";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { ScenarioCard } from "../../components/ScenarioCard";
import { selectedScenariosAtom } from "../../shared/state/atoms";
import {
  getScenarioList,
  getSituation,
  ScenarioModel,
} from "../../clients/KompasApi";
import { currentSituationAtom } from "./SituationModal";
import { usePagination } from "../../shared/hooks/usePagination";

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export function AddScenarioModal({ isOpen, onClose }: Props) {
  const [scenarios, setScenarios] = useState<ScenarioModel[]>([]);
  const [totalScenarioCount, setTotalScenarioCount] = useState(0);
  const [loadingSituation, setLoadingSituation] = useState(false);
  const [loadingScenarios, setLoadingScenarios] = useState(false);
  const { offset, limit, loadMore } = usePagination("Scenarios");
  const [currentSituation, setCurrentSituation] = useAtom(currentSituationAtom);

  const loadingScenarioCards = useMemo(
    () => loadingScenarios || loadingSituation,
    [loadingScenarios, loadingSituation],
  );

  const params = useParams<{
    residenceId: string;
    situationId: string;
    scenarioIds: string;
  }>();

  const navigate = useNavigate();

  const residenceTypeId = useMemo(
    () => Number(params.residenceId ?? "0"),
    [params.residenceId],
  );
  const situationId = useMemo(
    () => Number(params.situationId ?? "0"),
    [params.situationId],
  );
  const selectedScenarioIds = useMemo(
    () => params.scenarioIds?.split("&").map(Number).filter(Boolean) ?? [],
    [params.scenarioIds],
  );

  const [selectedScenarios, setSelectedScenarios] = useAtom(
    selectedScenariosAtom,
  );

  const [originalSelectedScenarios] = useState(selectedScenarioIds);

  useEffect(() => {
    if (currentSituation?.id === situationId) return;

    setLoadingSituation(true);

    const abortController = new AbortController();

    getSituation({
      path: { residenceTypeId, id: situationId },
      signal: abortController.signal,
      throwOnError: true,
    })
      .then(({ data }) => {
        if (abortController.signal.aborted) return;

        setCurrentSituation((curr) => {
          if (curr?.id === data?.id) return curr;
          return data;
        });
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;

        throw error;
      })
      .finally(() => setLoadingSituation(false));

    return () =>
      abortController.abort("Component unmounted or situationId changed");
  }, [residenceTypeId, situationId, currentSituation, setCurrentSituation]);

  useEffect(() => {
    if (currentSituation?.id !== situationId) return;

    setLoadingScenarios(true);

    const abortController = new AbortController();

    getScenarioList({
      path: { residenceTypeId, situationId },
      query: { Offset: offset, Limit: limit },
      signal: abortController.signal,
      throwOnError: true,
    })
      .then(({ data }) => {
        if (abortController.signal.aborted) return;

        if (data) {
          setScenarios((prev) => {
            prev.splice(offset, limit, ...(data.items ?? []));
            return prev.slice(0, offset + limit);
          });
          setTotalScenarioCount(
            data.totalCount ?? currentSituation.scenarioCount,
          );
        }
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;

        console.error("Error fetching scenarios:", error);
      })
      .finally(() => setLoadingScenarios(false));

    return () =>
      abortController.abort("Component unmounted or situationId changed");
  }, [residenceTypeId, offset, limit, situationId, currentSituation]);

  const availableScenarios = useMemo(
    () => scenarios.filter((s) => !selectedScenarioIds.includes(s.id)),
    [scenarios, selectedScenarioIds],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setSelectedScenarios(selectedScenarioIds), []);

  const selectedScenariosUnchanged = useMemo(
    () => selectedScenarios.join("&") === selectedScenarioIds.join("&"),
    [selectedScenarioIds, selectedScenarios],
  );

  const handleClose = () => {
    setSelectedScenarios(originalSelectedScenarios);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="half">
      <ModalOverlay />
      <ModalContent borderRadius={8}>
        <ModalHeader>Scenario toevoegen aan vergelijking</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {!!(scenarios.length && currentSituation) && (
            <Flex flexDir="column" gap={4}>
              {scenarios.map((s) => (
                <ScenarioCard
                  key={s.id}
                  scenario={s}
                  situation={currentSituation}
                />
              ))}
            </Flex>
          )}
          {!availableScenarios.length && (
            <Box fontWeight="500" my={8}>
              Er zijn geen scenario&apos;s beschikbaar om toe te voegen
            </Box>
          )}
          {totalScenarioCount < scenarios.length && (
            <Button
              isDisabled={loadingScenarioCards}
              onClick={loadMore}
              width="100%"
            >
              Meer scenario&apos;s laden
            </Button>
          )}
          {loadingScenarioCards && <Skeleton />}
        </ModalBody>

        <ModalFooter>
          <Button mr={4} onClick={handleClose} variant="outline">
            Annuleer
          </Button>
          <Button
            isDisabled={selectedScenariosUnchanged}
            onClick={() => {
              navigate(
                `../residence/${residenceTypeId}/situation/${situationId}/compare/${selectedScenarios.join(
                  "&",
                )}`,
              );
              onClose();
            }}
          >
            Toevoegen
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
